import { useState, useEffect, useCallback } from "react";
import { query, onSnapshot, orderBy, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { PDFDocument } from "pdf-lib";
import { storage, functions } from "../firebase";
import {
  requireTenantBandContext,
  tenantSetlistsCollectionRef,
  tenantSetlistDocRef,
} from "../tenantContext";
import { SoloTrigger } from "../features/upgrade/useUpgradeModal";

export function useSetlistManagement({
  tenantContext,
  user,
  songs,
  selectedSongs,
  setSelectedSongs,
  userRole,
  tierInfo,
  showSolo,
  requestConfirm,
  dismissConfirm,
  showToast,
  onNavigate,
}) {
  const [setlists, setSetlists] = useState([]);
  const [isSetlistsLoading, setIsSetlistsLoading] = useState(false);
  const [setlistsError, setSetlistsError] = useState("");
  const [editingSetlistId, setEditingSetlistId] = useState(null);
  const [setlistModalOpen, setSetlistModalOpen] = useState(false);
  const [setlistModalName, setSetlistModalName] = useState("");
  const [setlistModalDate, setSetlistModalDate] = useState("");
  const [downloadingSetlistId, setDownloadingSetlistId] = useState(null);

  // Real-time listener
  useEffect(() => {
    if (!user || !tenantContext?.bandId) return;
    setIsSetlistsLoading(true);
    setSetlistsError("");
    const tenant = requireTenantBandContext(tenantContext);
    const unsub = onSnapshot(
      query(tenantSetlistsCollectionRef(tenant), orderBy("createdAt", "desc")),
      (snap) => {
        setSetlists(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setIsSetlistsLoading(false);
      },
      (err) => {
        console.error("Error fetching setlists:", err);
        setSetlistsError("Could not load setlists.");
        setIsSetlistsLoading(false);
      }
    );
    return unsub;
  }, [user, tenantContext]);

  const cancelSetlistModal = useCallback(() => {
    setSetlistModalOpen(false);
    setSetlistModalName("");
    setSetlistModalDate("");
    setEditingSetlistId(null);
  }, []);

  const handleSaveSetlist = useCallback(async () => {
    if (userRole !== "admin") {
      alert("Only band admins can save setlists.");
      return;
    }
    if (selectedSongs.length === 0) {
      alert("Select at least one song before saving a setlist.");
      return;
    }

    if (editingSetlistId) {
      const currentSetlist = setlists.find((s) => s.id === editingSetlistId);
      try {
        const tenant = requireTenantBandContext(tenantContext);
        await updateDoc(tenantSetlistDocRef(tenant, editingSetlistId), {
          name: currentSetlist?.name || `Setlist ${new Date().toISOString().split("T")[0]}`,
          songIds: selectedSongs,
          performanceDate: currentSetlist?.performanceDate || null,
        });
        setSelectedSongs([]);
        setEditingSetlistId(null);
        onNavigate("setlists");
      } catch (err) {
        console.error("Error saving setlist:", err);
        alert("Could not save setlist. Please try again.");
      }
      return;
    }

    if (setlists.length >= tierInfo.setlistLimit) {
      showSolo(SoloTrigger.SETLIST_LIMIT_REACHED);
      return;
    }
    setSetlistModalName(`Setlist ${new Date().toISOString().split("T")[0]}`);
    setSetlistModalDate("");
    setSetlistModalOpen(true);
  }, [userRole, selectedSongs, editingSetlistId, setlists, tenantContext, tierInfo, showSolo, setSelectedSongs, onNavigate]);

  const handleConfirmSaveSetlist = useCallback(async () => {
    const name = setlistModalName.trim();
    if (!name) return;
    try {
      const tenant = requireTenantBandContext(tenantContext);
      const payload = {
        name,
        songIds: selectedSongs,
        performanceDate: setlistModalDate || null,
      };
      if (editingSetlistId) {
        await updateDoc(tenantSetlistDocRef(tenant, editingSetlistId), payload);
      } else {
        const createSetlist = httpsCallable(functions, "createSetlist");
        await createSetlist({ bandId: tenant.bandId, ...payload });
      }
      setSelectedSongs([]);
      setEditingSetlistId(null);
      setSetlistModalOpen(false);
      setSetlistModalName("");
      setSetlistModalDate("");
      onNavigate("setlists");
    } catch (err) {
      console.error("Error saving setlist:", err);
      showToast("Couldn't save setlist — please try again.");
    }
  }, [setlistModalName, setlistModalDate, editingSetlistId, selectedSongs, tenantContext, setSelectedSongs, showToast, onNavigate]);

  const handleRenameSetlist = useCallback((setlist) => {
    setEditingSetlistId(setlist.id);
    setSelectedSongs(Array.isArray(setlist.songIds) ? setlist.songIds : []);
    setSetlistModalName(setlist.name || "");
    setSetlistModalDate(setlist.performanceDate || "");
    setSetlistModalOpen(true);
  }, [setSelectedSongs]);

  const handleEditSetlist = useCallback((setlist) => {
    if (!Array.isArray(setlist.songIds) || setlist.songIds.length === 0) {
      showToast("This setlist has no songs yet.");
      return;
    }
    const availableSongIds = new Set(songs.map((s) => s.id));
    const filteredSongIds = setlist.songIds.filter((id) => availableSongIds.has(id));
    if (filteredSongIds.length === 0) {
      showToast("None of the songs in this setlist are available in your library.");
      return;
    }
    setSelectedSongs(filteredSongIds);
    setEditingSetlistId(setlist.id);
    onNavigate("songs");
  }, [songs, setSelectedSongs, showToast, onNavigate]);

  const handleDeleteSetlist = useCallback((setlistId, setlistName) => {
    if (userRole !== "admin") {
      showToast("Only admins can delete setlists.");
      return;
    }
    requestConfirm({
      title: "Delete setlist",
      message: `"${setlistName}" will be permanently deleted. This can't be undone.`,
      confirmLabel: "Delete setlist",
      danger: true,
      onConfirm: async () => {
        dismissConfirm();
        try {
          const tenant = requireTenantBandContext(tenantContext);
          await deleteDoc(tenantSetlistDocRef(tenant, setlistId));
        } catch (err) {
          console.error("Error deleting setlist:", err);
          showToast("Couldn't delete setlist — please try again.");
        }
      },
    });
  }, [userRole, tenantContext, requestConfirm, dismissConfirm, showToast]);

  const handleLockSetlist = useCallback(async (setlistId) => {
    if (userRole !== "admin") return false;
    try {
      const tenant = requireTenantBandContext(tenantContext);
      await updateDoc(tenantSetlistDocRef(tenant, setlistId), {
        locked: true,
        lockedAt: serverTimestamp(),
      });
      return true;
    } catch (err) {
      console.error("Error locking setlist:", err);
      showToast("Couldn't lock setlist — please try again.");
      return false;
    }
  }, [userRole, tenantContext, showToast]);

  const handleUnlockSetlist = useCallback(async (setlistId) => {
    if (userRole !== "admin") return;
    try {
      const tenant = requireTenantBandContext(tenantContext);
      await updateDoc(tenantSetlistDocRef(tenant, setlistId), {
        locked: false,
        lockedAt: null,
      });
    } catch (err) {
      console.error("Error unlocking setlist:", err);
      showToast("Couldn't unlock setlist — please try again.");
    }
  }, [userRole, tenantContext, showToast]);

  const mergePDFs = useCallback(async (songIds = selectedSongs, setlistId = null, setlistName = null) => {
    if (!tierInfo.canMergePdf) {
      showSolo(SoloTrigger.PDF_UPLOAD_LIMIT_REACHED);
      return;
    }
    if (songIds.length === 0) {
      alert("Please select at least one song to create a setlist.");
      return;
    }
    setSetlistsError("");
    setDownloadingSetlistId(setlistId);
    try {
      const mergedPdf = await PDFDocument.create();
      for (const songId of songIds) {
        const song = songs.find((s) => s.id === songId);
        if (!song) continue;
        let storagePath = song.pdfPath;
        if (!storagePath && song.pdfUrl) {
          try {
            const u = new URL(song.pdfUrl);
            const match = u.pathname.match(/\/o\/(.+)/);
            if (match) storagePath = decodeURIComponent(match[1]);
          } catch {
            // unparseable URL — storagePath stays null
          }
        }
        if (!storagePath) {
          console.warn(`Skipping "${song.title}": no storage path available`);
          continue;
        }
        const downloadUrl = await getDownloadURL(ref(storage, storagePath));
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error(`Failed to fetch PDF for "${song.title}"`);
        const pdfBytes = await response.arrayBuffer();
        const pdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${setlistName || "Setlist"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error merging PDFs:", err);
      setSetlistsError("Failed to download setlist PDF. Please try again.");
    } finally {
      setDownloadingSetlistId(null);
    }
  }, [selectedSongs, songs, tierInfo, showSolo]);

  return {
    setlists,
    isSetlistsLoading,
    setlistsError,
    editingSetlistId,
    setlistModalOpen,
    setlistModalName,
    setlistModalDate,
    setSetlistModalName,
    setSetlistModalDate,
    downloadingSetlistId,
    handleSaveSetlist,
    handleConfirmSaveSetlist,
    handleRenameSetlist,
    handleEditSetlist,
    handleDeleteSetlist,
    handleLockSetlist,
    handleUnlockSetlist,
    cancelSetlistModal,
    mergePDFs,
  };
}
